import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { Checkbox } from '@shared/components/ui/shadcn/checkbox';
import { Input } from '@shared/components/ui/shadcn/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { AlertTriangle, CheckCircle2, FileText, Info, Upload, X } from 'lucide-react';
import { useToast } from '@shared/hooks/use-toast';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntryReceiptRequirement } from '../../../models/Entry';
import { NON_COMPLIANCE_LABELS, NON_COMPLIANCE_REQUIREMENT_CODES } from '../../models/Receive';

export interface RequirementsModalRef {
  open: (
    requirements: EntryReceiptRequirement[],
    onCreate: (requirementCode: string, attachments?: File[]) => Promise<void>,
    readOnly?: boolean,
  ) => void;
}

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const getFileExtension = (fileName: string): string => {
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() || '' : '';
};

export const RequirementsModal = forwardRef<RequirementsModalRef>((_, ref) => {
  const { t } = useTranslation('merchandiseV3');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const [requirements, setRequirements] = useState<EntryReceiptRequirement[]>([]);
  const [pendingCodes, setPendingCodes] = useState<Set<string>>(new Set()); // Códigos marcados pero sin enviar
  const [attachmentsByCode, setAttachmentsByCode] = useState<Record<string, File[]>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingCode, setProcessingCode] = useState<string | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const createCallbackRef = useRef<((requirementCode: string, attachments?: File[]) => Promise<void>) | null>(null);

  useImperativeHandle(ref, () => ({
    open: (
      requirementsData: EntryReceiptRequirement[],
      onCreate: (requirementCode: string, attachments?: File[]) => Promise<void>,
      readOnly: boolean = false,
    ) => {
      setRequirements(requirementsData);
      setIsReadOnly(readOnly);
      setPendingCodes(new Set());
      setAttachmentsByCode({});
      setIsProcessing(false);
      setProcessingCode(null);
      createCallbackRef.current = onCreate;
      modalRef.current?.openModal();
    },
  }));

  const handleToggleRequirement = (code: string, checked: boolean) => {
    if (isProcessing || isReadOnly) return;

    if (checked) {
      setPendingCodes((prev) => new Set(prev).add(code));
    } else {
      setPendingCodes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(code);
        return newSet;
      });
      setAttachmentsByCode((prev) => {
        const newAttachments = { ...prev };
        delete newAttachments[code];
        return newAttachments;
      });
    }
  };

  const handleConfirmRequirement = async (code: string) => {
    if (isProcessing || processingCode === code || !createCallbackRef.current || isReadOnly) return;

    const attachments = attachmentsByCode[code] || [];
    if (attachments.length === 0) {
      return;
    }

    setProcessingCode(code);
    setIsProcessing(true);

    try {
      await createCallbackRef.current(code, attachments);

      setPendingCodes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(code);
        return newSet;
      });
    } catch (error) {
      console.error('=== handleConfirmRequirement error ===', error);
    } finally {
      setIsProcessing(false);
      setProcessingCode(null);
    }
  };

  const handleFileChange = (code: string, files: FileList | null) => {
    if (!files || isReadOnly) return;

    const newFiles = Array.from(files).slice(0, 5);
    const merged = [...(attachmentsByCode[code] || []), ...newFiles];

    for (const file of newFiles) {
      const extension = getFileExtension(file.name);
      const validType = ACCEPTED_MIME_TYPES.includes(file.type) || ACCEPTED_EXTENSIONS.includes(extension);

      if (!validType) {
        toast({
          title: 'Error',
          description: `Archivo "${file.name}" no válido. Solo se permiten JPG, JPEG, PNG o PDF`,
          variant: 'destructive',
        });
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast({
          title: 'Error',
          description: `Archivo "${file.name}" excede el límite de 5MB`,
          variant: 'destructive',
        });
        return;
      }
    }

    setAttachmentsByCode((prev) => ({
      ...prev,
      [code]: merged.slice(0, 5),
    }));
  };

  const handleRemoveFile = (code: string, index: number) => {
    if (isReadOnly) return;
    setAttachmentsByCode((prev) => ({
      ...prev,
      [code]: prev[code]?.filter((_, i) => i !== index) || [],
    }));
  };

  // Vista de solo lectura
  if (isReadOnly) {
    const confirmedRequirements = requirements.filter((req) =>
      NON_COMPLIANCE_REQUIREMENT_CODES.includes(req.requirement_code as any),
    );

    return (
      <ModalCenter
        ref={modalRef}
        title={t('requirements.modal.title_readonly', 'Incumplimientos Registrados')}
        type="info"
        width="lg"
      >
        <div className="space-y-4">
          {confirmedRequirements.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-foreground mb-2">
                {t('requirements.modal.no_issues', 'Sin incumplimientos')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('requirements.modal.no_issues_description', 'Esta recepción no registró ningún incumplimiento')}
              </p>
            </div>
          ) : (
            <>
              <Alert className="bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800">
                <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                <AlertDescription className="text-sm text-orange-800 dark:text-orange-200">
                  {t(
                    'requirements.modal.readonly_info',
                    'Esta recepción está finalizada. Los incumplimientos no pueden ser modificados.',
                  )}
                </AlertDescription>
              </Alert>

              <ScrollArea className="max-h-[400px]">
                <div className="space-y-3 pr-4">
                  {confirmedRequirements.map((requirement) => {
                    const label = NON_COMPLIANCE_LABELS[requirement.requirement_code] || requirement.requirement_code;

                    return (
                      <div
                        key={requirement.requirement_code}
                        className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4"
                      >
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-medium text-foreground">{label}</span>
                              <Badge variant="destructive" className="text-xs">
                                {t('requirements.modal.confirmed', 'Confirmado')}
                              </Badge>
                            </div>

                            {/* Mostrar archivos adjuntos si existen */}
                            {requirement.attachments && requirement.attachments.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs text-muted-foreground mb-1">
                                  {t('requirements.modal.evidence', 'Evidencias:')}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {requirement.attachments.map((attachmentUrl, index) => (
                                    <a
                                      key={index}
                                      href={attachmentUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-xs text-primary hover:underline bg-background px-2 py-1 rounded border border-border"
                                    >
                                      <FileText className="w-3 h-3" />
                                      {attachmentUrl.split('/').pop() || `Archivo ${index + 1}`}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </>
          )}
        </div>
      </ModalCenter>
    );
  }

  // Vista de edición (original)
  return (
    <ModalCenter
      ref={modalRef}
      title={t('requirements.modal.title', 'Gestionar Incumplimientos')}
      type="info"
      width="lg"
    >
      <div className="space-y-4">
        {/* Lista de incumplimientos con checkboxes */}
        <div className="space-y-3">
          <div className="space-y-3">
            {NON_COMPLIANCE_REQUIREMENT_CODES.map((code) => {
              const isChecked = requirements.some((r) => r.requirement_code === code);
              const isPending = pendingCodes.has(code);
              const isProcessingThis = processingCode === code;
              const label = NON_COMPLIANCE_LABELS[code] || code;
              const attachments = attachmentsByCode[code] || [];
              const existingRequirement = requirements.find((r) => r.requirement_code === code);
              const serverAttachments = existingRequirement?.attachments || [];
              const showSection = isChecked || isPending;

              return (
                <div
                  key={code}
                  className={`rounded-lg border transition-all ${
                    showSection
                      ? 'border-primary/30 bg-primary/5 ring-1 ring-primary/20'
                      : 'border-border bg-background hover:border-primary/20'
                  }`}
                >
                  {/* Header con checkbox */}
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      {!isChecked && (
                        <Checkbox
                          id={`requirement-${code}`}
                          checked={isPending}
                          onCheckedChange={(checked) => handleToggleRequirement(code, checked === true)}
                          disabled={isProcessing && !isProcessingThis}
                          className="mt-0.5 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <Label htmlFor={`requirement-${code}`} className="cursor-pointer flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-foreground">{label}</span>
                          {isChecked && (
                            <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                              {t('requirements.modal.confirmed', 'Confirmado')}
                            </span>
                          )}
                          {isPending && (
                            <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                              {t('requirements.modal.pending', 'Pendiente')}
                            </span>
                          )}
                          {isProcessingThis && (
                            <span className="text-xs text-muted-foreground ml-auto">
                              {t('requirements.modal.processing', 'Procesando...')}
                            </span>
                          )}
                        </Label>

                        {/* Sección de archivos adjuntos - visible cuando está marcado */}
                        {showSection && (
                          <div className="mt-3 space-y-3">
                            {!isChecked && (
                              <>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,image/jpeg,image/png,application/pdf"
                                    multiple
                                    onChange={(e) => handleFileChange(code, e.target.files)}
                                    className="hidden"
                                    id={`requirement-files-${code}`}
                                    disabled={isProcessing}
                                  />
                                  <Label
                                    htmlFor={`requirement-files-${code}`}
                                    className="flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-md cursor-pointer hover:bg-accent transition-colors bg-background"
                                  >
                                    <Upload className="w-4 h-4" />
                                    {t('requirements.modal.upload_files', 'Subir archivos')}
                                  </Label>
                                  {attachments.length > 0 && (
                                    <span className="text-xs text-muted-foreground">
                                      {attachments.length} {attachments.length === 1 ? 'archivo' : 'archivos'}
                                    </span>
                                  )}
                                </div>

                                {attachments.length > 0 && (
                                  <div className="space-y-1.5">
                                    {attachments.map((file, index) => (
                                      <div
                                        key={index}
                                        className="flex items-center justify-between bg-muted/50 rounded-md px-3 py-2 text-sm border border-border"
                                      >
                                        <span className="truncate flex-1 text-foreground">{file.name}</span>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveFile(code, index)}
                                          disabled={isProcessing}
                                          className="text-destructive hover:text-destructive/80 ml-3 flex-shrink-0 p-1"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}

                            {serverAttachments.length > 0 && (
                              <>
                                {attachments.length > 0 && (
                                  <p className="text-xs text-muted-foreground mt-2">Evidencias existentes:</p>
                                )}
                                <div className="flex flex-wrap gap-2">
                                  {serverAttachments.map((attachmentUrl, index) => (
                                    <a
                                      key={index}
                                      href={attachmentUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-xs text-primary hover:underline bg-background px-2 py-1 rounded border border-border"
                                    >
                                      <FileText className="w-3 h-3" />
                                      {attachmentUrl.split('/').pop() || `Archivo ${index + 1}`}
                                    </a>
                                  ))}
                                </div>
                              </>
                            )}

                            {isPending && (
                              <>
                                <Button
                                  type="button"
                                  variant="primary"
                                  onClick={() => handleConfirmRequirement(code)}
                                  disabled={isProcessing || attachments.length === 0}
                                  className="w-full"
                                >
                                  {isProcessing && processingCode === code
                                    ? t('requirements.modal.processing', 'Procesando...')
                                    : t('requirements.modal.confirm', 'Confirmar Incumplimiento')}
                                </Button>
                                {attachments.length === 0 && (
                                  <p className="text-xs text-muted-foreground text-center">
                                    {t(
                                      'requirements.modal.attachments_required',
                                      'Debe subir al menos una evidencia para confirmar el incumplimiento',
                                    )}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Información sobre incumplimientos */}
        <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
            {t(
              'requirements.modal.info',
              'Los incumplimientos marcados afectarán automáticamente el resultado de la recepción y extenderán el plazo de entrega.',
            )}
          </AlertDescription>
        </Alert>
      </div>
    </ModalCenter>
  );
});

RequirementsModal.displayName = 'RequirementsModal';
